import DataProvider from "./data-provider";
import {from} from "linq";
import {applyPageSettings, applySortSettings} from "./data-provider-helpers";
import {Subject} from "rxjs";
import moment from "moment";

/**
 * A data provider for a locally sourced array that doesnt change often
 * its more useful for when you do not want to hit an API multiple times
 * and can just set the data once
 */
export default class ArrayDataProvider extends DataProvider
{
    /**
     *
     * @param {Array} data The array of data to usecarcin
     * @param {Array} textFilterFields Optional fields to allow text filtration against
     * @param {string} dateFilterField Optional field to allow date filtration against
     */
    constructor(data, textFilterFields = [], dateFilterField= "")
    {
        super();
        this.onDataSourceChanged = new Subject();
        this.textFilterFields = textFilterFields;
        this.dateFilterField = dateFilterField;
        this.updateDataSource(data);
    }

    /**
     * Updates the underlying datasource to a new array
     * @param data The new data to apply
     */
    updateDataSource = (data) =>
    {
        this.enumerableData = from(data);
        this.onDataSourceChanged.next();
    };

    /**
     * Predicate for fields checking against text
     * @param elementToSearch The element to search
     * @param textFilter The text to filter upon
     * @return {boolean} A truthy indicating if the text is included or not
     */
    checkFieldsForText = (elementToSearch, textFilter) => {
        for(const field of this.textFilterFields)
        {
            if(elementToSearch[field].toLowerCase().indexOf(textFilter)>= 0)
            { return true; }
        }
        return false;
    };

    /**
     * Predicate for field checking against date range
     * @param elementToSearch The element to search
     * @param fromDate The minimum date range
     * @param toDate The maximum date range
     * @return {boolean} A truthy indicating if the elements date field is within a valid date range
     */
    checkFieldsForDateRange = (elementToSearch, fromDate, toDate) => {
        const fromDateMoment = moment(fromDate ? fromDate : toDate);
        const toDateMoment = moment(toDate);

        if(!fromDate)
        { fromDateMoment.subtract(12, "months"); }

        const dateToCheck = moment(elementToSearch[this.dateFilterField]);
        return (dateToCheck >= fromDate && dateToCheck <= toDateMoment);
    };

    /**
     * Filters the array data as required
     * @param {any} context Contextual data required to return data
     * @return {Promise} returns a promise containing the data (elements field) and optional other fields
     */
    refreshData = (context) =>
    {
        let filteredData = this.enumerableData;

        if(context.sortSettings)
        { filteredData = applySortSettings(filteredData, context.sortSettings); }

        if(context.filterSettings)
        {
            if(this.textFilterFields && this.textFilterFields.length > 0 && context.filterSettings.text && context.filterSettings.text.length > 0)
            { filteredData = filteredData.where(x => this.checkFieldsForText(x, context.filterSettings.text.toLowerCase())); }

            if(this.dateFilterField && this.dateFilterField.length > 0 && (context.filterSettings.fromDate || context.filterSettings.toDate))
            { filteredData = filteredData.where(x => this.checkFieldsForDateRange(x , context.filterSettings.fromDate, context.filterSettings.toDate)); }
        }

        if(context.pageSettings && !context.isExporting)
        { filteredData = applyPageSettings(filteredData, context.pageSettings); }

        return Promise.resolve({
            elements: filteredData.toArray(),
            totalCount: this.enumerableData.count()
        });
    };

    /**
     * Returns the current underlying array data
     * @return {[]} The current array being used by the data provider
     */
    getData = () => {
        return this.enumerableData.toArray();
    }
}